import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  InputAdornment,
  Chip,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalShipping as LocalShippingIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  Visibility as VisibilityIcon,
  LockOutlined as LockOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  ShoppingCart as ShoppingCartIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import VistaConduce from './VistaConduce';

export const PROGRAMA_REGISTRO_CONDUCES = 'registrarconduces';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

const RegistroConduce = () => {
  const { controller, selectedCompany, showSuccess, showError } =
    useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_REGISTRO_CONDUCES),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_REGISTRO_CONDUCES),
    [roleDetalles]
  );

  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [conductor, setConductor] = useState('');
  const [direccionenvio, setDireccionenvio] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [observaciones, setObservaciones] = useState('');
  const [itemsConduce, setItemsConduce] = useState([]);

  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [dialogoProductoAbierto, setDialogoProductoAbierto] = useState(false);
  const [cantidadAgregar, setCantidadAgregar] = useState(1);
  const [cantidadesPorProducto, setCantidadesPorProducto] = useState({});

  const getCantidadProducto = (codigo) => cantidadesPorProducto[codigo] ?? 1;
  const setCantidadProducto = (codigo, valor) => {
    const num = Math.max(0.01, parseFloat(valor) || 1);
    setCantidadesPorProducto((prev) => ({ ...prev, [codigo]: num }));
  };

  const [vistaConduceAbierta, setVistaConduceAbierta] = useState(false);
  const [datosVistaConduce, setDatosVistaConduce] = useState({});

  const subtotal = itemsConduce.reduce(
    (sum, item) => sum + (parseFloat(item.subtotal) || 0),
    0
  );

  const totalItbis = itemsConduce.reduce((sum, item) => {
    const sub = parseFloat(item.subtotal) || 0;
    const tasa = parseFloat(item.itbis) || 0;
    return sum + (tasa > 0 ? sub * (tasa / 100) : 0);
  }, 0);

  const total = subtotal + totalItbis;

  const cargarProductos = useCallback(async () => {
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerproductos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: 'productosnoserializados',
          lista_precio_id: null,
          localidad: localStorage.getItem('sucursal') || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setProductos(data.productos || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selectedCompany, servidor, usuarioLog, claveLog]);

  const cargarClientes = useCallback(async () => {
    try {
      const response = await fetch(servidor + '/api/clientesbackend_buscar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo: 'Cliente',
          descripcion: '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setClientes(data.resultados || data.clientes || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [selectedCompany, servidor, usuarioLog, claveLog]);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      await Promise.all([cargarProductos(), cargarClientes()]);
    } catch {
      showError('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  }, [cargarProductos, cargarClientes, showError]);

  useEffect(() => {
    if (permisoLeer) cargarDatos();
  }, [permisoLeer, cargarDatos]);

  const obtenerConfiguracionParaTipo = async (tipoDoc) => {
    const nombreTipoTrim = (tipoDoc || '').trim();
    const intentos = [];
    if (nombreTipoTrim) intentos.push(nombreTipoTrim);
    const upper = nombreTipoTrim.toUpperCase();
    if (upper && !intentos.includes(upper)) intentos.push(upper);
    for (const fallback of ['CONDUCE', 'Conduce']) {
      if (!intentos.includes(fallback)) intentos.push(fallback);
    }
    let ultimaRespuesta = null;
    for (const intento of intentos) {
      try {
        const response = await fetch(
          servidor + '/api/facturabackend_obtenerconfiguracion',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: intento,
            }),
          }
        );
        if (!response.ok) continue;
        const data = await response.json();
        ultimaRespuesta = data?.configuracion || ultimaRespuesta;
        if (data?.configuracionGuardada && data?.configuracion) {
          return data.configuracion;
        }
      } catch (_) {
        // intento siguiente
      }
    }
    return ultimaRespuesta;
  };

  const agregarProducto = (producto, cantidad = 1) => {
    if (!permisoModificar) {
      showError('No tiene permiso para modificar el conduce');
      return;
    }
    const cant = Math.max(0.01, parseFloat(cantidad) || 1);
    const precio = parseFloat(producto.precio) || 0;
    const itbis = parseFloat(producto.itbis) || 0;
    const nuevoItem = {
      id: Date.now() + Math.random(),
      codigo: producto.codigo,
      descripcion: producto.descripcion,
      cantidad: cant,
      precio,
      subtotal: cant * precio,
      itbis,
      unidad: producto.medida || 'UND',
      medida: producto.medida,
    };
    const existente = itemsConduce.find((i) => i.codigo === producto.codigo);
    if (existente) {
      setItemsConduce((prev) =>
        prev.map((item) => {
          if (item.id !== existente.id) return item;
          const nuevaCant = parseFloat(item.cantidad) + cant;
          return {
            ...item,
            cantidad: nuevaCant,
            subtotal: nuevaCant * (parseFloat(item.precio) || 0),
          };
        })
      );
    } else {
      setItemsConduce((prev) => [...prev, nuevoItem]);
    }
    setDialogoProductoAbierto(false);
    setBusquedaProducto('');
    setCantidadAgregar(1);
    setCantidadesPorProducto((prev) => {
      const { [producto.codigo]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const eliminarItem = (id) => {
    if (!permisoModificar) return;
    setItemsConduce((prev) => prev.filter((item) => item.id !== id));
  };

  const actualizarCantidad = (id, nuevaCantidad) => {
    if (!permisoModificar) return;
    const num = parseFloat(nuevaCantidad);
    if (isNaN(num) || num <= 0) return;
    setItemsConduce((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          cantidad: num,
          subtotal: num * (parseFloat(item.precio) || 0),
        };
      })
    );
  };

  const actualizarPrecio = (id, nuevoPrecio) => {
    if (!permisoModificar) return;
    const num = parseFloat(nuevoPrecio);
    if (isNaN(num) || num < 0) return;
    setItemsConduce((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        return {
          ...item,
          precio: num,
          subtotal: (parseFloat(item.cantidad) || 0) * num,
        };
      })
    );
  };

  const guardarConduce = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para registrar conduces');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (itemsConduce.length === 0) {
      showError('Agregue al menos un ítem');
      return;
    }
    if (!clienteSeleccionado) {
      showError('Seleccione un cliente');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(servidor + '/api/conducebackend_guardarconduce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: 'productosnoserializados',
          localidad: localStorage.getItem('sucursal') || '',
          conduce: {
            cliente: String(clienteSeleccionado.id),
            items: itemsConduce.map((i) => ({
              codigo: i.codigo,
              descripcion: i.descripcion,
              cantidad: i.cantidad,
              precio: i.precio,
              subtotal: i.subtotal,
              itbis: i.itbis || 0,
              unidad: i.unidad || 'UND',
              medida: i.medida,
            })),
            fecha,
            usuario: usuarioLog || '',
            observaciones: observaciones.trim(),
            conductor: conductor.trim(),
            direccionenvio: direccionenvio.trim(),
            factura: '',
            nfact: null,
          },
        }),
      });
      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || `Conduce ${data.numero} guardado correctamente`);
        const configuracion = await obtenerConfiguracionParaTipo('CONDUCE');
        setDatosVistaConduce({
          numero: data.numero,
          documento: data.documento || 'CONDUCE',
          fecha,
          cliente_nombre:
            clienteSeleccionado?.descrip || clienteSeleccionado?.descripcion || '',
          conductor: conductor.trim(),
          direccionenvio: direccionenvio.trim(),
          observaciones: observaciones.trim(),
          items: itemsConduce.map((i) => ({
            id: i.id,
            codigo: i.codigo,
            descripcion: i.descripcion,
            cantidad: i.cantidad,
            precio: i.precio,
            subtotal: i.subtotal,
            itbis: i.itbis || 0,
            unidad: i.unidad || 'UND',
          })),
          subtotal,
          total_itbis: totalItbis,
          total,
          empresa: selectedCompany,
          id_empresa: selectedCompany,
          configuracion,
        });
        setVistaConduceAbierta(true);
        limpiarConduce();
      } else {
        const err = await response.json();
        showError(err.Error || 'Error al guardar el conduce');
      }
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const limpiarConduce = () => {
    setItemsConduce([]);
    setObservaciones('');
    setConductor('');
    setDireccionenvio('');
    setFecha(new Date().toISOString().split('T')[0]);
  };

  const abrirVistaConduce = async () => {
    if (itemsConduce.length === 0) {
      showError('Agregue al menos un ítem para ver la vista previa');
      return;
    }
    if (!clienteSeleccionado) {
      showError('Seleccione un cliente');
      return;
    }
    const configuracion = await obtenerConfiguracionParaTipo('CONDUCE');
    setDatosVistaConduce({
      numero: '',
      documento: 'CONDUCE',
      fecha,
      cliente_nombre:
        clienteSeleccionado?.descrip || clienteSeleccionado?.descripcion || '',
      conductor: conductor.trim(),
      direccionenvio: direccionenvio.trim(),
      observaciones: observaciones.trim(),
      items: itemsConduce.map((i) => ({
        id: i.id,
        codigo: i.codigo,
        descripcion: i.descripcion,
        cantidad: i.cantidad,
        precio: i.precio,
        subtotal: i.subtotal,
        unidad: i.unidad || 'UND',
      })),
      subtotal,
      total: subtotal,
      empresa: selectedCompany,
      id_empresa: selectedCompany,
      configuracion,
    });
    setVistaConduceAbierta(true);
  };

  const productosFiltrados = useMemo(() => {
    if (!busquedaProducto.trim()) return productos.slice(0, 100);
    const b = busquedaProducto.toLowerCase();
    return productos
      .filter(
        (p) =>
          String(p.codigo || '').toLowerCase().includes(b) ||
          String(p.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 100);
  }, [productos, busquedaProducto]);

  const clientesFiltrados = useMemo(() => {
    if (!busquedaCliente.trim()) return clientes.slice(0, 100);
    const b = busquedaCliente.toLowerCase();
    return clientes
      .filter(
        (c) =>
          String(c.id || '').includes(b) ||
          String(c.descrip || c.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 100);
  }, [clientes, busquedaCliente]);

  // --- Acceso denegado ---------------------------------------------------

  if (!permisoLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Conduce"
          subtitle={nombreNegocio ? `${nombreNegocio} · Registro` : 'Registro'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 560,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «{PROGRAMA_REGISTRO_CONDUCES}» en roles para registrar conduces.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  // --- Render principal --------------------------------------------------

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Conduce"
        subtitle={nombreNegocio ? `${nombreNegocio} · Registro` : 'Registro'}
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {!permisoModificar && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.warningSoft,
            }}
          >
            Solo lectura: tiene permiso de consulta pero no puede registrar ni modificar conduces.
          </Alert>
        )}

        {/* Bloque: datos del conduce */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del conduce
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clientesFiltrados}
                  getOptionLabel={(opt) =>
                    `${opt.id || ''} - ${opt.descrip || opt.descripcion || ''}`
                  }
                  value={clienteSeleccionado}
                  onChange={(_, val) => setClienteSeleccionado(val)}
                  inputValue={busquedaCliente}
                  onInputChange={(_, val) => setBusquedaCliente(val)}
                  disabled={!permisoModificar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      required
                      placeholder="Buscar cliente…"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={observaciones}
                  label="Observaciones"
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Observaciones del conduce…"
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { paddingTop: '3.8em' } }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Conductor"
                  value={conductor}
                  onChange={(e) => setConductor(e.target.value)}
                  placeholder="Nombre del conductor"
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalShippingIcon sx={{ color: FP.color.textMuted, fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Dirección de envío"
                  value={direccionenvio}
                  onChange={(e) => setDireccionenvio(e.target.value)}
                  placeholder="Dirección de entrega"
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalShippingIcon sx={{ color: FP.color.textMuted, fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bloque: ítems y totales */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Ítems del Conduce
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogoProductoAbierto(true)}
              disabled={cargando || !permisoModificar}
              size="small"
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              }}
            >
              Agregar producto
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                maxHeight: 360,
              }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell sx={cellHeadSx}>Código</TableCell>
                    <TableCell sx={cellHeadSx}>Descripción</TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Cant.
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Unidad
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Precio
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Subtotal
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                  {itemsConduce.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                          No hay ítems en el conduce. Use «Agregar producto».
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemsConduce.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={row.codigo}
                            size="small"
                            sx={{
                              bgcolor: FP.color.accentBlueSoft,
                              color: FP.color.accentBlue,
                              fontWeight: 700,
                              fontSize: '0.74rem',
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 500, color: FP.color.textPrimary, fontSize: '0.86rem' }}
                          title={row.descripcion}
                        >
                          {row.descripcion}
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0.01, step: 0.1, style: { textAlign: 'center' } }}
                            value={row.cantidad}
                            disabled={!permisoModificar}
                            onChange={(e) => actualizarCantidad(row.id, e.target.value)}
                            sx={{ width: 90, ...fpInputSx }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ color: FP.color.textSecondary }}>
                          {row.unidad || 'UND'}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                            value={row.precio}
                            disabled={!permisoModificar}
                            onChange={(e) => actualizarPrecio(row.id, e.target.value)}
                            sx={{ width: 110, ...fpInputSx }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: FP.color.navy,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '0.92rem',
                          }}
                        >
                          ${fmtMoney(row.subtotal)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => eliminarItem(row.id)}
                            disabled={!permisoModificar}
                            sx={{
                              color: FP.color.danger,
                              '&:hover': { bgcolor: FP.color.dangerSoft },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totales */}
            <Box
              sx={{
                mt: 2,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                  Subtotal:
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(subtotal)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                  ITBIS:
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(totalItbis)}
                </Typography>
              </Box>
              <Divider sx={{ my: 1.5, borderColor: FP.color.border }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 1.6,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Total:</Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(total)}
                </Typography>
              </Box>
            </Box>

            {/* Acciones */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={abrirVistaConduce}
                disabled={itemsConduce.length === 0 || !clienteSeleccionado}
                fullWidth
                sx={{
                  height: 50,
                  flex: 1,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Vista previa
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limpiarConduce}
                disabled={!permisoModificar}
                fullWidth
                sx={{
                  height: 50,
                  flex: 1,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={guardarConduce}
                fullWidth
                disabled={
                  guardando ||
                  itemsConduce.length === 0 ||
                  !clienteSeleccionado ||
                  !permisoModificar ||
                  !usuarioLog
                }
                sx={{
                  height: 50,
                  flex: 1,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 800,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted },
                }}
              >
                {guardando ? 'Guardando…' : 'Guardar Conduce'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Diálogo agregar producto */}
      <Dialog
        open={dialogoProductoAbierto}
        onClose={() => {
          setDialogoProductoAbierto(false);
          setCantidadesPorProducto({});
          setBusquedaProducto('');
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 800, fontSize: '1.05rem', color: FP.color.textPrimary }}
          >
            Agregar producto
          </Typography>
          <IconButton
            onClick={() => {
              setDialogoProductoAbierto(false);
              setCantidadesPorProducto({});
              setBusquedaProducto('');
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: FP.color.surface, px: 3, pb: 3, pt: 0 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mt: 2.5,
              mb: 2.5,
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Autocomplete
              fullWidth
              freeSolo
              autoHighlight
              openOnFocus
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
              options={productos}
              filterOptions={(opts, { inputValue }) => {
                const b = String(inputValue || '').trim().toLowerCase();
                if (!b) return opts.slice(0, 50);
                return opts
                  .filter(
                    (p) =>
                      String(p.codigo || '').toLowerCase().includes(b) ||
                      String(p.descripcion || '').toLowerCase().includes(b)
                  )
                  .slice(0, 50);
              }}
              getOptionLabel={(opt) =>
                typeof opt === 'string'
                  ? opt
                  : `${opt.codigo || ''} - ${opt.descripcion || ''}`
              }
              isOptionEqualToValue={(opt, val) => opt?.codigo === val?.codigo}
              inputValue={busquedaProducto}
              onInputChange={(_, val, reason) => {
                if (reason === 'reset') return;
                setBusquedaProducto(val);
              }}
              onChange={(_, val) => {
                if (val && typeof val !== 'string') {
                  const cant = getCantidadProducto(val.codigo) || cantidadAgregar;
                  agregarProducto(val, cant);
                }
              }}
              noOptionsText="No hay productos que coincidan"
              renderOption={(props, opt) => (
                <Box
                  component="li"
                  {...props}
                  key={opt.codigo}
                  sx={{
                    display: 'flex !important',
                    alignItems: 'center !important',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    <Chip
                      label={opt.codigo}
                      size="small"
                      sx={{
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.accentBlue,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.86rem',
                        color: FP.color.textPrimary,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={opt.descripcion}
                    >
                      {opt.descripcion}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: FP.color.textPrimary,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  >
                    ${fmtMoney(opt.precio || 0)}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Buscar producto"
                  placeholder="Por código o descripción…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: FP.color.textMuted }}
                            fontSize="small"
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              type="number"
              label="Cantidad por defecto"
              value={cantidadAgregar}
              onChange={(e) =>
                setCantidadAgregar(Math.max(0.01, parseFloat(e.target.value) || 1))
              }
              inputProps={{
                min: 0.01,
                step: 0.01,
                style: { textAlign: 'center' },
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0, ...fpInputSx }}
            />
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              maxHeight: 520,
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell sx={{ ...cellHeadSx, width: 110 }}>Código</TableCell>
                  <TableCell sx={cellHeadSx}>Descripción</TableCell>
                  <TableCell sx={{ ...cellHeadSx, textAlign: 'right', width: 110 }} align="right">
                    Precio
                  </TableCell>
                  <TableCell sx={{ ...cellHeadSx, textAlign: 'center', width: 100 }} align="center">
                    Cant.
                  </TableCell>
                  <TableCell sx={{ ...cellHeadSx, width: 110 }} />
                </TableRow>
                {productosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                        No hay productos o no coincide la búsqueda.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productosFiltrados.map((p) => (
                    <TableRow key={p.codigo} hover>
                      <TableCell sx={{ fontSize: '0.84rem', color: FP.color.textPrimary }}>
                        {p.codigo}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          py: 1.2,
                          fontSize: '0.85rem',
                          color: FP.color.textPrimary,
                        }}
                        title={p.descripcion}
                      >
                        {p.descripcion}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '0.86rem',
                          color: FP.color.textPrimary,
                        }}
                      >
                        ${fmtMoney(p.precio || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={getCantidadProducto(p.codigo)}
                          onChange={(e) => setCantidadProducto(p.codigo, e.target.value)}
                          inputProps={{
                            min: 0.01,
                            step: 0.01,
                            style: { textAlign: 'center' },
                          }}
                          sx={{ width: 90, ...fpInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => agregarProducto(p, getCantidadProducto(p.codigo))}
                          sx={{
                            bgcolor: FP.color.navy,
                            color: '#FFFFFF',
                            borderRadius: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                          }}
                        >
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}`, px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDialogoProductoAbierto(false);
              setCantidadesPorProducto({});
              setBusquedaProducto('');
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <VistaConduce
        open={vistaConduceAbierta}
        onClose={() => setVistaConduceAbierta(false)}
        conduceData={datosVistaConduce}
      />
    </Box>
  );
};

export default RegistroConduce;
